import { useCallback } from 'react'
import Collapse from 'antd/lib/collapse'
import Input from 'antd/lib/input'
import Radio from 'antd/lib/radio'
import Space from 'antd/lib/space'
import MessageOutlined from '@ant-design/icons/lib/icons/MessageOutlined'
import LikeOutlined from '@ant-design/icons/lib/icons/LikeOutlined'
import DislikeOutlined from '@ant-design/icons/lib/icons/DislikeOutlined'
import styles from './CSATFeedbackCollapse.module.less'
import type { CSATFeedbackConfig } from 'Types/CSAT.types'

export type CSATChoice = 'yes' | 'no' | null

export type CSATFeedbackValue = {
    expanded: boolean
    rating: number | null
    comment: string
    choices: Record<string, CSATChoice>
}

type CSATFeedbackCollapseProps = {
    feedbackConfig?: CSATFeedbackConfig
    value: CSATFeedbackValue
    onChange: (next: CSATFeedbackValue) => void
}

export const CSATFeedbackCollapse: React.FC<CSATFeedbackCollapseProps> = ({
    feedbackConfig,
    value,
    onChange,
}) => {
    const surveyPanelKey = 'csat-survey'

    const isRatingEnabled = feedbackConfig?.rating_enabled ?? true
    const isCommentEnabled = feedbackConfig?.comment_enabled ?? true
    const questions = feedbackConfig?.reaction_questions ?? []

    const setReactionChoice = useCallback(
        (questionTitle: string, choice: Exclude<CSATChoice, null>) => {
            const current = value.choices[questionTitle] ?? null
            const choices = { ...value.choices }

            if (current === choice) {
                delete choices[questionTitle]
            } else {
                choices[questionTitle] = choice
            }

            onChange({ ...value, choices })
        },
        [value, onChange]
    )

    return (
        <Collapse
            className={styles.surveyCollapse}
            bordered
            expandIconPosition="end"
            activeKey={value.expanded ? [surveyPanelKey] : []}
            onChange={keys => {
                const nextKeys = Array.isArray(keys) ? keys : [keys]
                onChange({ ...value, expanded: nextKeys.includes(surveyPanelKey) })
            }}
        >
            <Collapse.Panel
                key={surveyPanelKey}
                header={
                    <Space size={12} className={styles.surveyHeader}>
                        <span className={styles.surveyIcon}>
                            <MessageOutlined />
                        </span>
                        <span>Share additional feedback about the project</span>
                    </Space>
                }
            >
                <div className={styles.panelScroll}>
                    <div className={styles.surveyBody}>
                        {isRatingEnabled && (
                            <>
                                <div className={styles.surveyQuestionTitle}>
                                    Overall, how satisfied were you with your experience
                                    contributing to this project?
                                </div>
                                <div className={styles.ratingRow}>
                                    <Radio.Group
                                        value={value.rating ?? undefined}
                                        className={styles.ratingGroup}
                                    >
                                        {Array.from({ length: 10 }, (_, i) => i + 1).map(num => (
                                            <Radio.Button
                                                key={num}
                                                value={num}
                                                onClick={() => {
                                                    const newRating =
                                                        value.rating === num ? null : num
                                                    onChange({
                                                        ...value,
                                                        rating: newRating,
                                                    })
                                                }}
                                            >
                                                {num}
                                            </Radio.Button>
                                        ))}
                                    </Radio.Group>
                                </div>
                                <div className={styles.ratingLabels}>
                                    <span>Not Satisfied-</span>
                                    <span>-Very Satisfied</span>
                                </div>
                            </>
                        )}

                        {questions.length > 0 && (
                            <div className={styles.secondaryList}>
                                {questions.map(q => {
                                    const selected = value.choices[q.question_title] ?? null

                                    return (
                                        <div key={q.question_title} className={styles.secondaryRow}>
                                            <span className={styles.secondaryText}>
                                                {q.question_text}
                                            </span>
                                            <Radio.Group
                                                value={selected}
                                                className={styles.thumbs}
                                                size="middle"
                                            >
                                                <Radio.Button
                                                    value="yes"
                                                    onClick={() =>
                                                        setReactionChoice(q.question_title, 'yes')
                                                    }
                                                    className={styles.thumbButton}
                                                >
                                                    <LikeOutlined />
                                                </Radio.Button>
                                                <Radio.Button
                                                    value="no"
                                                    onClick={() =>
                                                        setReactionChoice(q.question_title, 'no')
                                                    }
                                                    className={styles.thumbButton}
                                                >
                                                    <DislikeOutlined />
                                                </Radio.Button>
                                            </Radio.Group>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {isCommentEnabled && (
                            <div className={styles.surveyComment}>
                                <div className={styles.surveyCommentLabel}>
                                    Anything else you want to share
                                </div>
                                <Input.TextArea
                                    value={value.comment}
                                    onChange={e =>
                                        onChange({
                                            ...value,
                                            comment: e.target.value,
                                        })
                                    }
                                    placeholder="Use this for overall project feedback"
                                    rows={4}
                                    className={styles.commentTextArea}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Collapse.Panel>
        </Collapse>
    )
}
